<template>
  <div class="renderPage pln-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="pln-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-tag-multiple-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase pln-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Planes
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="pln-btn font-weight-bold" @click="incluir()">
        <v-icon left small>mdi-plus</v-icon> Nuevo Plan
      </v-btn>
    </v-toolbar>

    <!-- TABLA -->
    <v-card flat class="pln-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="52" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar plan..."
          single-line hide-details outlined dense
          class="pln-search"
          style="max-width:400px"
        ></v-text-field>
        <v-spacer />
        <v-btn icon :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'grey darken-1'" @click="getData()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        class="pln-table"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.id`]="{ item }">
          <span class="pln-code-badge">{{ item.id }}</span>
        </template>

        <template v-slot:[`item.monto`]="{ item }">
          <span class="font-weight-bold">₡{{ Number(item.monto).toLocaleString('es-CR', { minimumFractionDigits: 2 }) }}</span>
        </template>

        <template v-slot:[`item.ilimitado`]="{ item }">
          <v-chip x-small :color="item.ilimitado ? 'success' : 'blue-grey'" text-color="white" class="font-weight-bold" label style="border-radius:3px">
            {{ item.ilimitado ? 'Ilimitado' : 'Limitado' }}
          </v-chip>
        </template>

        <template v-slot:[`item.cantidadDocumentos`]="{ item }">
          <span>{{ item.ilimitado ? '∞' : item.cantidadDocumentos }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark
            style="min-width:36px;height:36px;padding:0;border-radius:4px"
            @click.stop="editItem(item)">
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-tag-multiple-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron planes.</p>
            <v-btn color="primary" depressed small @click="getData()">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- DRAWER FORMULARIO -->
    <v-navigation-drawer v-model="drawerForm" fixed right temporary width="520" class="pln-drawer">
      <div class="d-flex flex-column fill-height">

        <!-- HEADER -->
        <div class="pln-drawer-header d-flex align-center px-4 py-3"
             :class="$vuetify.theme.dark ? 'pln-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">{{ editedIndex > -1 ? 'mdi-tag-edit-outline' : 'mdi-tag-plus-outline' }}</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex > -1 ? 'Editar Plan' : 'Nuevo Plan' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">{{ editedItem.nombre || 'Información del plan' }}</div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- CUERPO -->
        <div class="flex-grow-1 overflow-y-auto pa-5">
          <v-form v-model="valid" ref="form">

            <div class="pln-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-tag-outline</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Datos del Plan</span>
            </div>
            <v-card flat class="pln-section-card pa-4 mb-4">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.nombre"
                    label="Nombre del Plan *"
                    outlined dense
                    :rules="[v => !!v || 'Requerido']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="editedItem.monto"
                    label="Monto (₡) *"
                    outlined dense type="number"
                    :rules="[v => v >= 0 || 'Debe ser mayor o igual a 0']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="editedItem.tipoPlanId"
                    label="Tipo de Plan ID"
                    outlined dense type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card>

            <div class="pln-section-label mb-3">
              <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-tune</v-icon>
              <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Límites</span>
            </div>
            <v-card flat class="pln-section-card pa-4 mb-4">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="editedItem.cantidadUsuarios"
                    label="Cantidad de Usuarios"
                    outlined dense type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="editedItem.cantidadDocumentos"
                    label="Cantidad de Documentos"
                    outlined dense type="number"
                    :disabled="editedItem.ilimitado"
                    :hint="editedItem.ilimitado ? 'No aplica para plan ilimitado' : ''"
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-switch
                    v-model="editedItem.ilimitado"
                    label="Plan Ilimitado (sin restricción de documentos)"
                    color="primary"
                    inset
                    hide-details
                  ></v-switch>
                </v-col>
              </v-row>
            </v-card>

          </v-form>
        </div>

        <!-- FOOTER -->
        <div class="pln-drawer-footer d-flex align-center px-4 py-3"
             :class="$vuetify.theme.dark ? 'pln-drawer-footer--dark' : ''">
          <v-btn v-if="editedIndex > -1" color="error" text class="pln-btn font-weight-bold" :loading="loadingDelete" @click="deleteItem()">
            <v-icon left small>mdi-trash-can-outline</v-icon> Eliminar
          </v-btn>
          <v-spacer />
          <v-btn text class="mr-3 font-weight-bold pln-btn" @click="cerrar()">Cancelar</v-btn>
          <v-btn color="primary" depressed class="px-8 font-weight-bold pln-btn" :loading="loading" @click="save()">
            {{ editedIndex > -1 ? 'GUARDAR' : 'CREAR' }}
          </v-btn>
        </div>

      </div>
    </v-navigation-drawer>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "Planes",
  components: { Snackbar },
  data() {
    return {
      drawerForm: false,
      loading: false,
      loadingDelete: false,
      valid: false,
      search: "",
      items: [],
      editedIndex: -1,
      editedItem: {
        id: 0, nombre: "", monto: 0, ilimitado: false,
        tipoPlanId: 1, cantidadUsuarios: 1, cantidadDocumentos: 0,
      },
      defaultItem: {
        id: 0, nombre: "", monto: 0, ilimitado: false,
        tipoPlanId: 1, cantidadUsuarios: 1, cantidadDocumentos: 0,
      },
      headers: [
        { text: "ID",         value: "id",                   width: "70"                },
        { text: "Nombre",     value: "nombre"                                            },
        { text: "Monto",      value: "monto",         align: "end"                      },
        { text: "Tipo",       value: "ilimitado",     align: "center"                   },
        { text: "Usuarios",   value: "cantidadUsuarios",  align: "center", width: "100" },
        { text: "Documentos", value: "cantidadDocumentos", align: "center", width: "120"},
        { text: "Acciones",   value: "actions", align: "right", sortable: false         },
      ],
      snackText: "",
      snackValue: false,
    };
  },
  computed: {
    ...mapGetters(["user"]),
  },
  created() {
    this.getData();
  },
  methods: {
    getData() {
      axios.get("api/Licencias/getPlanes")
        .then(r => { this.items = r.data; })
        .catch(e => console.error(e));
    },
    incluir() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.drawerForm = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.drawerForm = true;
    },
    cerrar() {
      this.drawerForm = false;
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.loading = true;
      const url = this.editedIndex > -1
        ? "api/Licencias/updatePlan"
        : "api/Licencias/insertPlan";
      axios.post(url, this.editedItem)
        .then(() => {
          this.snackText = "Plan guardado correctamente.";
          this.snackValue = true;
          this.getData();
          this.cerrar();
        })
        .catch(e => {
          this.snackText = "Error al guardar el plan.";
          this.snackValue = true;
          console.error(e);
        })
        .finally(() => { this.loading = false; });
    },
    deleteItem() {
      if (!confirm(`¿Eliminar el plan "${this.editedItem.nombre}"?`)) return;
      this.loadingDelete = true;
      axios.get("api/Licencias/deletePlan?id=" + this.editedItem.id)
        .then(() => {
          this.snackText = "Plan eliminado.";
          this.snackValue = true;
          this.getData();
          this.cerrar();
        })
        .catch(e => {
          this.snackText = "Error al eliminar el plan.";
          this.snackValue = true;
          console.error(e);
        })
        .finally(() => { this.loadingDelete = false; });
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.pln-container { background-color: #f1f5f9; min-height: 100vh; }
.theme--dark.pln-container { background-color: #0f172a; }

/* ===== TOOLBAR ===== */
.pln-toolbar { border-bottom: 1px solid rgba(0,0,0,0.1) !important; }
.theme--dark .pln-toolbar { border-bottom: 1px solid rgba(255,255,255,0.07) !important; }
.pln-toolbar-title { font-size: 0.95rem !important; letter-spacing: 1.5px; }

/* ===== BUTTONS ===== */
.pln-btn { border-radius: 4px !important; text-transform: uppercase; letter-spacing: 0.8px; font-size: 0.75rem !important; }

/* ===== TABLE ===== */
.pln-table-card { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.pln-table { background: transparent !important; }
.pln-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.pln-table >>> tbody tr { cursor: pointer; }
.pln-table >>> tbody tr:hover { background-color: rgba(55,71,79,0.05) !important; }
.theme--dark .pln-table >>> tbody tr:hover { background-color: rgba(255,255,255,0.05) !important; }

/* ===== CODE BADGE ===== */
.pln-code-badge {
  font-family: monospace; font-size: 0.8rem; font-weight: 700;
  padding: 2px 6px; background: rgba(55,71,79,0.08); border-radius: 3px; color: #37474f;
}
.theme--dark .pln-code-badge { background: rgba(255,255,255,0.1); color: #b0bec5; }

/* ===== SEARCH ===== */
.pln-search >>> .v-input__slot { border-radius: 4px !important; }

/* ===== DRAWER ===== */
.pln-drawer >>> .v-navigation-drawer__content { background-color: #ffffff !important; }
.theme--dark .pln-drawer >>> .v-navigation-drawer__content { background-color: #0f172a !important; }

/* ===== DRAWER HEADER ===== */
.pln-drawer-header { background-color: #37474f; flex-shrink: 0; }
.pln-drawer-header--dark { background-color: #263238 !important; }

/* ===== DRAWER FOOTER ===== */
.pln-drawer-footer { background-color: #f8fafc; border-top: 1px solid rgba(0,0,0,0.08); flex-shrink: 0; }
.pln-drawer-footer--dark { background-color: #1e293b !important; border-top: 1px solid rgba(255,255,255,0.07) !important; }

/* ===== SECTION ===== */
.pln-section-label { display: flex; align-items: center; }
.pln-section-card { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; background-color: #ffffff !important; }
.theme--dark .pln-section-card { border-color: rgba(255,255,255,0.07) !important; background-color: #1e293b !important; }
</style>
